import _ from 'underscore';
import PropTypes from 'prop-types';
import { validFormats, formValidationSchema } from './config';
import { expressionTokenTypes, textInputToken } from './token';

/**
 * Props used by HoC and also passed to child
 */
export const sharedProps = {
  metricCategories: PropTypes.arrayOf(PropTypes.shape({
    titleKey: PropTypes.string.isRequired,
    metrics: PropTypes.arrayOf(PropTypes.shape({
      metric: PropTypes.string.isRequired,
      titleKey: PropTypes.string.isRequired,
    }).isRequired).isRequired,
  }).isRequired).isRequired,
  segmentCategories: PropTypes.arrayOf(PropTypes.shape({
    category: PropTypes.string.isRequired,
    titleKey: PropTypes.string.isRequired,
    segments: PropTypes.arrayOf(PropTypes.shape({
      segment: PropTypes.string.isRequired,
      titleKey: PropTypes.string.isRequired,
    })).isRequired,
  })),

  // provided by @bingads-webui-react/hoc-with-do-with-closest-handler
  containerRef: PropTypes.func,
  doWithClosest: PropTypes.func,
  setChildRefByIndex: PropTypes.func,
  setChildRefCount: PropTypes.func,
};

export const noopFactory = () => _.noop;

export const sharedDefaults = {
  containerRef: _.noop,
  doWithClosest: action => action({}),
  metricCategories: [],
  segmentCategories: [],
  setChildRefByIndex: noopFactory,
  setChildRefCount: _.noop,
};

/**
 * Schema for an individual ExpressionToken
 */
export const expressionTokenProp = PropTypes.shape({
  segments: PropTypes.shape({
    categoryValue: PropTypes.string,
    categoryTitle: PropTypes.string,
    selections: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      title: PropTypes.string,
    })),
  }),
  showSuggestions: PropTypes.bool,
  title: PropTypes.string,
  type: PropTypes.oneOf(_.values(expressionTokenTypes)).isRequired,
  value: PropTypes.string.isRequired,
});

/**
 * New props created by this HoC and passed to child
 */
export const providesProps = {
  containerRef: PropTypes.func.isRequired,
  currentFocus: PropTypes.number,
  mostRecentMetricFocus: PropTypes.number,
  customColumnFormSchema: PropTypes.shape({
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    properties: PropTypes.object.isRequired,
  }).isRequired,
  customColumnFormErrorMessages: PropTypes.arrayOf(PropTypes.string),
  customColumnFormIsDirty: PropTypes.bool,
  doWithClosest: PropTypes.func.isRequired,
  expressionString: PropTypes.string,
  expressionTokens: PropTypes.arrayOf(expressionTokenProp),
  formValueName: PropTypes.string,
  formValueDescription: PropTypes.string,
  formValueColumnFormat: PropTypes.string,
  isFormValid: PropTypes.bool.isRequired,
  isValid: PropTypes.bool,
  isExpressionTooLong: PropTypes.bool,
  onDropdownValueSelectedFactory: PropTypes.func.isRequired,
  onFocusFactory: PropTypes.func.isRequired,
  onFormValueChange: PropTypes.func.isRequired,
  onFormValidate: PropTypes.func.isRequired,
  onInputChangeFactory: PropTypes.func.isRequired,
  onInputClickFactory: PropTypes.func.isRequired,
  onInputKeyUpFactory: PropTypes.func.isRequired,
  onMetricKeyUpFactory: PropTypes.func.isRequired,
  onMetricRemoveButtonClickFactory: PropTypes.func.isRequired,
  onMetricSelected: PropTypes.func.isRequired,
  onOperatorSelected: PropTypes.func.isRequired,
  onSegmentChangedFactory: PropTypes.func.isRequired,
  setChildRefByIndex: PropTypes.func.isRequired,
  setChildRefCount: PropTypes.func.isRequired,
  setFocus: PropTypes.func.isRequired,
};

const defaultExpressionTokens = [textInputToken()];

export const providesDefaults = {
  containerRef: _.noop,
  currentFocus: -1,
  mostRecentMetricFocus: -1,
  customColumnFormSchema: formValidationSchema,
  customColumnFormErrorMessages: [],
  customColumnFormIsDirty: false,
  doWithClosest: _.noop,
  expressionString: '',
  expressionTokens: defaultExpressionTokens,
  formValueName: '',
  formValueDescription: '',
  formValueColumnFormat: _.first(validFormats),
  isFormValid: false,
  isValid: false,
  isExpressionTooLong: false,
  onDropdownValueSelectedFactory: noopFactory,
  onFocusFactory: noopFactory,
  onFormValueChange: _.noop,
  onFormValidate: _.noop,
  onInputChangeFactory: noopFactory,
  onInputClickFactory: noopFactory,
  onInputKeyUpFactory: noopFactory,
  onMetricKeyUpFactory: noopFactory,
  onMetricRemoveButtonClickFactory: noopFactory,
  onMetricSelected: _.noop,
  onOperatorSelected: _.noop,
  onSegmentChangedFactory: noopFactory,
  setChildRefByIndex: noopFactory,
  setChildRefCount: _.noop,
  setFocus: _.noop,
};
